import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AvatarLabel, Select, Popup } from '_atoms';
import { Typography } from '_atoms/Typography';
import { getUsersByRole } from '_molecules/PopupAssignRecruiters/PopupAssignRecruiters';
import { fetchWrapper } from 'helpers/helpers';
import { getUser } from 'store/selectors/authorizeSelectors';
import { ACTIONS, ROLES, UI, useAbility } from 'permission';
import './styles.scss';

import { isOptionGroup } from '_atoms/Select';
import type { SelectOption } from '_atoms/Select/types';
import type { UserSimple } from 'store/reducers/types';

interface RecruiterOption extends SelectOption {
  value: string;
  label: string;
  avatar?: string | null;
}

interface RecruiterOptionGroup {
  label: string;
  options: RecruiterOption[];
}

interface PopupReassignRecruiterProps {
  open: boolean | number;
  onClose: () => void;
  onConfirm: (reassignedRecruiters: Record<string, RecruiterOption>) => void;
  recruitersReassignFrom: UserSimple[];
}

const getRecruiters = (body: Record<string, unknown>) =>
  fetchWrapper({
    url: 'users/',
    body,
  }).catch(() => []);

export const PopupReassignRecruiter: React.FC<PopupReassignRecruiterProps> = ({
  open,
  onClose,
  onConfirm,
  recruitersReassignFrom,
}) => {
  const { t } = useTranslation();
  const ability = useAbility();
  const user = useSelector(getUser);

  const [recruitersOptions, setRecruitersOptions] = useState<(RecruiterOption | RecruiterOptionGroup)[]>([]);
  const [reassignedRecruiters, setReassignedRecruiters] = useState<Record<string, RecruiterOption>>({});

  const assignRecruiterLead = ability.can(ACTIONS.READ, UI.ASSIGN_RECRUITER_LEAD);
  const isRL = user.active_role?.name === ROLES.RECRUITER_LEAD;

  const cancelButtonProps = {
    onClick: onClose,
    children: t('translation:popup_reassign_recruiter.button_cancel'),
  };

  const okButtonProps = {
    onClick: () => onConfirm(reassignedRecruiters),
    children: t('translation:popup_reassign_recruiter.button_reassign'),
    disabled: Object.keys(reassignedRecruiters).length !== recruitersReassignFrom.length,
  };

  const onSelectNewRecruiter = (oldRecruiter: string, newRecruiter: string) => {
    setReassignedRecruiters(r => ({
      ...r,
      [oldRecruiter]:
        assignRecruiterLead || isRL
          ? recruitersOptions
              .reduce(
                (options: RecruiterOption[], option) => [
                  ...options,
                  ...(isOptionGroup(option) ? option.options : [option]),
                ],
                []
              )
              .find(option => option.value === newRecruiter)
          : (recruitersOptions as RecruiterOption[]).find(option => option.value === newRecruiter),
    }));
  };

  useEffect(() => {
    if (open) {
      getRecruiters({
        role: assignRecruiterLead ? [ROLES.RECRUITER_LEAD, ROLES.RECRUITER] : ROLES.RECRUITER,
        exclude_uuid__in: recruitersReassignFrom.map(r => r.uuid).join(','),
        my_recruiters_group: isRL,
      }).then((resp: unknown) => {
        const listOptions: RecruiterOptionGroup[] = [];
        const recruiterLeadOptions: RecruiterOption[] = assignRecruiterLead
          ? getUsersByRole(resp, ROLES.RECRUITER_LEAD)
          : [];
        if (isRL && !recruitersReassignFrom.map(r => r.uuid).includes(user.uuid))
          recruiterLeadOptions.push({ value: user.uuid, label: user.full_name });
        const recruiterOptions: RecruiterOption[] = getUsersByRole(
          resp,
          ROLES.RECRUITER,
          recruiterLeadOptions.map(u => u.value)
        );
        if (recruiterLeadOptions.length)
          listOptions.push({ label: ROLES.RECRUITER_LEAD, options: recruiterLeadOptions });
        if (recruiterOptions.length) listOptions.push({ label: ROLES.RECRUITER, options: recruiterOptions });
        const newApproversOptions = assignRecruiterLead || isRL ? listOptions : getUsersByRole(resp);
        setRecruitersOptions(newApproversOptions);
      });
    } else {
      setRecruitersOptions([]);
      setReassignedRecruiters({});
    }
  }, [open]);

  return (
    <Popup
      open={open}
      title={t('translation:popup_reassign_recruiter.title')}
      cancelButtonProps={cancelButtonProps}
      okButtonProps={okButtonProps}
    >
      <div className="popup-reassign-recruiter">
        <Typography.Text className="popup-reassign-recruiter__subtitle">
          {t('translation:popup_reassign_recruiter.subtitle')}
        </Typography.Text>
        {recruitersReassignFrom.map((r, idx) => (
          <div key={r.full_name + idx.toString()} className="popup-reassign-recruiter__recruiter-item">
            <AvatarLabel name={r.full_name} src={r.avatar} extraName={ROLES.RECRUITER} />
            <Select
              className="popup-reassign-recruiter__select"
              options={recruitersOptions}
              label={t('translation:popup_reassign_recruiter.label_reassign_to')}
              placeholder="Select responsible Recruiter"
              value={reassignedRecruiters[r.uuid]}
              onChange={({ value }: { value: string }) => onSelectNewRecruiter(r.uuid, value)}
              inPopup
            />
          </div>
        ))}
      </div>
    </Popup>
  );
};
